import { BodyText, Flex, Strong } from '@utilitywarehouse/hearth-react';

import {
  EnergyMeterType,
  EnergyPaymentMethod,
  FuelBreakdown,
} from '@generated/gql/graphql';
import { formatCurrency, formatNumber } from '@utils/format';

import { getAnnualCharge } from './getAnnualCharge';
import { Breakdown } from './types';

const getElectricityConsumptionText = (electricity: FuelBreakdown) => {
  const { usageDay, usageNight, meterType } = electricity;
  const isEcon7 = meterType === EnergyMeterType.Economy_7;

  if (isEcon7) {
    return `${formatNumber(usageDay as number)}/${formatNumber(
      usageNight ?? 0
    )} kWh (day/night) of electricity `;
  }

  return `${formatNumber(usageDay as number)} kWh of electricity`;
};

const getGasConsumptionText = (gas: FuelBreakdown) =>
  `${formatNumber(gas.usageDay as number)} kWh of gas`;

const getConsumptionText = (
  gas: Nullable<FuelBreakdown>,
  electricity: Nullable<FuelBreakdown>
) => {
  if (gas && electricity) {
    return `${getElectricityConsumptionText(
      electricity
    )} and ${getGasConsumptionText(gas as FuelBreakdown)}`;
  }

  if (gas && !electricity) {
    return getGasConsumptionText(gas);
  }

  if (!gas && electricity) {
    return getElectricityConsumptionText(electricity);
  }

  return '';
};

const Calculations = ({ monthlyCost, electricity, gas }: Breakdown) => {
  const consumptionText = getConsumptionText(
    gas as FuelBreakdown,
    electricity as FuelBreakdown
  );
  const isPrepayment =
    electricity?.paymentMethod === EnergyPaymentMethod.Prepayment &&
    gas?.paymentMethod === EnergyPaymentMethod.Prepayment;

  return (
    <BodyText>
      Your estimated annual usage is {consumptionText}, which costs{' '}
      <Strong>{formatCurrency(getAnnualCharge(monthlyCost))}</Strong> a year.
      {!isPrepayment && (
        <>
          {' '}
          This is 12 monthly payments of{' '}
          <Strong>{formatCurrency(monthlyCost)}</Strong>, which we'll review
          throughout the year as we learn how much energy you use.
        </>
      )}
    </BodyText>
  );
};

const CostBreakdown = ({ monthlyCost, electricity, gas }: Breakdown) => (
  <Flex gap="100" direction="column" className="energy-breakdown__cost-section">
    <BodyText weight="semibold" className="energy-tariff__subtitle">
      How we calculate your monthly cost
    </BodyText>
    <Calculations
      monthlyCost={monthlyCost}
      electricity={electricity}
      gas={gas}
    />
  </Flex>
);

export { CostBreakdown };
